import React, {useContext, useEffect, useMemo, useState} from "react";
import {
  Box, Chip, Grid, IconButton, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import {IconTrash} from "@tabler/icons-react";
import {toast} from "react-toastify";
import {UserRepository} from "../../repositories/UserRepository";
import {SignInContext} from "../../context/SignInContext";
import useConfirm from "../../hooks/useConfirm";

export const Users = () => {
  const {currentUser} = useContext(SignInContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({page: 0, size: 10});

  const confirmDelete = useConfirm(
    "Are you sure you want to delete this user?",
    "This action cannot be undone. If the user still has related data, deletion will be blocked and you should deactivate the account instead."
  );

  const fetchUsers = () => {
    setLoading(true);
    UserRepository.getAll()
      .then(response => setUsers(response?.data || []))
      .catch(() => toast.error("Failed to get users"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchUsers();
  }, [])

  const handleToggleStatus = (user) => {
    const enabled = !user?.enabled;
    UserRepository.updateStatus(user?.username, enabled)
      .then(() => {
        toast.success(`Successfully ${enabled ? "activated" : "deactivated"} user`);
        setUsers(prev => prev.map(u => u.username === user.username ? {...u, enabled} : u));
      })
      .catch(() => toast.error(`Failed to ${enabled ? "activate" : "deactivate"} user`));
  }

  const handleDelete = async (user) => {
    const response = await confirmDelete({title: `Are you sure you want to delete "${user?.username}"?`});
    if (!response) return;

    UserRepository.delete(user?.username)
      .then(() => {
        toast.success("Successfully deleted user");
        setUsers(prev => prev.filter(u => u.username !== user.username));
      })
      .catch(error => {
        if (error?.response?.status === 409) {
          toast.error(error?.response?.data?.message || "This user still has related data. Deactivate the account instead.");
        } else {
          toast.error(error?.response?.data?.message || "Failed to delete user");
        }
      })
  }

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(user => {
      const fullName = `${user?.name || ""} ${user?.lastname || ""}`.toLowerCase();
      return fullName.includes(query)
        || user?.username?.toLowerCase()?.includes(query)
        || user?.email?.toLowerCase()?.includes(query)
        || user?.phoneNumber?.toLowerCase()?.includes(query);
    });
  }, [users, search]);

  const paginatedUsers = filteredUsers.slice(
    pagination.page * pagination.size,
    pagination.page * pagination.size + pagination.size
  );

  return (
    <Grid maxWidth={"lg"} sx={{mx: "auto", p: 3, my: 5, borderRadius: 4, bgcolor: "#fefefeee"}} container
          className={"border-shadow"}>
      <Grid item sm={12}>
        <Stack direction={"row"} gap={1} justifyContent={"space-between"} alignItems={"center"} sx={{mb: 2}}>
          <Box maxWidth={"md"}>
            <Typography variant={"h4"} fontWeight={600}>Users</Typography>
          </Box>
          <TextField
            size={"small"}
            placeholder={"Search users..."}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination(prev => ({...prev, page: 0}));
            }}
          />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{fontWeight: 700}}>Full Name</TableCell>
              <TableCell sx={{fontWeight: 700}}>Username</TableCell>
              <TableCell sx={{fontWeight: 700}}>Email</TableCell>
              <TableCell sx={{fontWeight: 700}}>Phone Number</TableCell>
              <TableCell sx={{fontWeight: 700}}>Roles</TableCell>
              <TableCell sx={{fontWeight: 700}}>Active</TableCell>
              <TableCell sx={{fontWeight: 700}}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers?.map(user => {
              const isSelf = user?.username === currentUser?.username;
              const roles = user?.authorityList?.map(item => item?.authority) || user?.roles || [];
              return (
                <TableRow key={user?.username}>
                  <TableCell>
                    <Typography>{`${user?.name || ""} ${user?.lastname || ""}`.trim() || "-"}</Typography>
                  </TableCell>
                  <TableCell>{user?.username}</TableCell>
                  <TableCell>{user?.email || "-"}</TableCell>
                  <TableCell>{user?.phoneNumber || "-"}</TableCell>
                  <TableCell>
                    <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
                      {roles?.map(role => (
                        <Chip key={role} label={role} size={"small"}/>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell padding={"checkbox"}>
                    <Switch
                      checked={!!user?.enabled}
                      onChange={() => handleToggleStatus(user)}
                    />
                  </TableCell>
                  <TableCell padding={"checkbox"}>
                    <Tooltip title={isSelf ? "You cannot delete your own account" : "Delete user"}>
                      <span>
                        <IconButton
                          variant={"contained"} color={"error"}
                          disabled={isSelf}
                          sx={{borderRadius: "50% !important"}}
                          onClick={() => handleDelete(user)}
                        >
                          <IconTrash size={18}/>
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
            {!loading && filteredUsers?.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography align={"center"} color={"text.secondary"} sx={{py: 3}}>No users found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredUsers?.length || 0}
          page={pagination.page}
          onPageChange={(e, page) => setPagination(prev => ({...prev, page}))}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={(e) => setPagination(prev => ({...prev, page: 0, size: +e.target.value}))}
        />
      </Grid>
    </Grid>
  )
}
